// src/pages/AdvisorLog.js
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useROStore } from "../context/ROContext";
import { useAuth } from "../context/AuthContext";
import { useROMessages } from "../context/ROMessagesContext";
import { useROUnread } from "../context/ROUnreadContext";
import ROStatusTimeline from "../components/ro/ROStatusTimeline";
import ROActivityDrawer from "../components/ro/ROActivityDrawer";
import {
  RO_STATUS,
  RO_LIFECYCLE,
  TRANSPORT_TYPES,
  WORK_MIX,
} from "../constants/roStatus";
import { normalizeROArray, filterROsByQuery } from "../utils/metricsEngine";

/* =========================
   Helpers
========================= */
function safeString(v) {
  return typeof v === "string" ? v : v == null ? "" : String(v);
}

function safeDate(input) {
  if (!input) return null;
  const d = new Date(input);
  return Number.isNaN(d.getTime()) ? null : d;
}

function toTime(input) {
  const d = safeDate(input);
  return d ? d.getTime() : null;
}

function nowISO() {
  return new Date().toISOString();
}

function formatTime(input) {
  const d = safeDate(input);
  if (!d) return "—";
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

function formatDate(input) {
  const d = safeDate(input);
  if (!d) return "—";
  return d.toLocaleDateString([], {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function formatPromise(input) {
  const d = safeDate(input);
  if (!d) return "—";
  return `${formatDate(d)} ${formatTime(d)}`;
}

function formatShortDateTime(input) {
  const d = safeDate(input);
  if (!d) return "—";
  return d.toLocaleString([], {
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function toDateTimeLocalValue(iso) {
  const d = safeDate(iso);
  if (!d) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
    d.getDate()
  )}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function normalizeVinLast8(v) {
  return safeString(v)
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(-8);
}

function getVinDisplay(ro) {
  return normalizeVinLast8(
    ro?.vinLast8 ||
      ro?.vin_last_8 ||
      ro?.vin_last8 ||
      ro?.vinLast ||
      ro?.vin ||
      ""
  );
}

function toUpperTrim(v) {
  return safeString(v).trim().toUpperCase();
}

function normalizeName(v) {
  return safeString(v).trim().toLowerCase();
}

function workMixLabel(v) {
  const s = String(v || "").toUpperCase();
  if (s === "MAINTENANCE") return "Maintenance";
  if (s === "DIAGNOSIS") return "Diagnosis";
  return "Other";
}

function transportLabel(v) {
  const s = String(v || "").toUpperCase();
  if (s === "WAITER") return "Waiter";
  if (s === "LOANER") return "Loaner";
  if (s === "SHUTTLE") return "Shuttle";
  if (s === "OTHER") return "Other";
  return "";
}

function getTag(ro) {
  return safeString(
    ro?.tagNumber ?? ro?.tag_number ?? ro?.tag ?? ro?.keyTag ?? ""
  ).trim();
}

function isArchivedLike(ro) {
  return (
    String(ro?.status || "").toUpperCase() ===
    String(RO_STATUS.ARCHIVED).toUpperCase()
  );
}

function isAssignedLike(ro) {
  return (
    !!safeString(ro?.assignedTechId).trim() ||
    !!safeString(ro?.assignedTechName).trim()
  );
}

function advisorDisplayName(ro) {
  return (
    safeString(
      ro?.advisorName || ro?.serviceAdvisorName || ro?.writerName || ""
    ).trim() || "—"
  );
}

function latestSharedNote(notes) {
  const raw = safeString(notes).trim();
  if (!raw) return "";

  const blocks = raw
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);

  if (!blocks.length) return "";

  const lines = blocks[blocks.length - 1]
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);

  if (lines.length <= 1) return lines[0] || "";
  return lines.slice(1).join(" ");
}

function normalizeLifecycle(lc) {
  const s = String(lc || "").toUpperCase();
  if (s === "WAITING_ON_PARTS") return RO_LIFECYCLE.WAITING_FOR_PARTS;
  if (s === "WAITING_FOR_PARTS") return RO_LIFECYCLE.WAITING_FOR_PARTS;
  if (s === "WAITING_FOR_ASSIGNMENT") return RO_LIFECYCLE.WAITING_FOR_ASSIGNMENT;
  if (s === "IN_PROGRESS") return RO_LIFECYCLE.IN_PROGRESS;
  if (s === "COMPLETED") return RO_LIFECYCLE.COMPLETED;
  if (s === "OPEN") return RO_LIFECYCLE.OPEN;
  return s || RO_LIFECYCLE.OPEN;
}

function getOperationalState(ro) {
  const status = safeString(ro?.status).toUpperCase();
  const lifecycleRaw = safeString(ro?.lifecycle || ro?.lifeCycle).toUpperCase();

  if (
    isArchivedLike(ro) ||
    status === "VEHICLE_GONE" ||
    lifecycleRaw === "VEHICLE_GONE"
  ) {
    return { key: "ARCHIVED", label: "Vehicle Gone" };
  }

  const lc = normalizeLifecycle(ro?.lifecycle || ro?.lifeCycle);

  if (lc === RO_LIFECYCLE.WAITING_FOR_PARTS) {
    return { key: "WAITING_PARTS", label: "Waiting Parts" };
  }

  if (lc === RO_LIFECYCLE.IN_PROGRESS) {
    return { key: "IN_PROGRESS", label: "In Progress" };
  }

  if (lc === RO_LIFECYCLE.WAITING_FOR_ASSIGNMENT) {
    return { key: "UNASSIGNED", label: "Unassigned" };
  }

  if (lc === RO_LIFECYCLE.COMPLETED) {
    return { key: "COMPLETED", label: "Completed" };
  }

  if (lc === RO_LIFECYCLE.OPEN && isAssignedLike(ro)) {
    return { key: "ASSIGNED", label: "Assigned" };
  }

  if (isAssignedLike(ro)) return { key: "ASSIGNED", label: "Assigned" };

  return { key: "UNASSIGNED", label: "Unassigned" };
}

function isCompletedLike(ro) {
  const status = safeString(ro?.status).toUpperCase();
  const lifecycle = normalizeLifecycle(ro?.lifecycle || ro?.lifeCycle);

  return (
    status === safeString(RO_STATUS.COMPLETED).toUpperCase() ||
    lifecycle === RO_LIFECYCLE.COMPLETED
  );
}

function isGoneLike(ro) {
  const status = safeString(ro?.status).toUpperCase();
  const lifecycle = safeString(ro?.lifecycle || ro?.lifeCycle).toUpperCase();

  const pickupStatus = safeString(
    ro?.customerPickupStatus || ro?.customer_pickup_status
  ).toUpperCase();

  const advisorAction = safeString(
    ro?.advisorActionStatus || ro?.advisor_action_status
  ).toUpperCase();

  return (
    pickupStatus === "VEHICLE_GONE" ||
    advisorAction === "VEHICLE_GONE" ||
    isArchivedLike(ro) ||
    status === "VEHICLE_GONE" ||
    lifecycle === "VEHICLE_GONE" ||
    !!ro?.vehicleGone ||
    !!ro?.vehicle_gone ||
    !!ro?.pickedUp ||
    !!ro?.picked_up ||
    !!ro?.pickedUpAt ||
    !!ro?.picked_up_at ||
    !!ro?.pickedUpAtISO ||
    !!ro?.picked_up_at_iso ||
    !!ro?.vehicleGoneAt ||
    !!ro?.vehicle_gone_at ||
    !!ro?.vehicleGoneAtISO ||
    !!ro?.vehicle_gone_at_iso ||
    !!ro?.timestamps?.vehicleGoneAt ||
    !!ro?.timestamps?.vehicle_gone_at ||
    !!ro?.timestamps?.pickedUpAt ||
    !!ro?.timestamps?.picked_up_at
  );
}

function isActiveAdvisorRO(ro, dateWindow = null) {
  if (!ro || isArchivedLike(ro) || isGoneLike(ro)) return false;

  /*
    Advisor Log keeps Vehicle Gone out of active work.

    Open, assigned, waiting-parts, and in-progress ROs stay visible until
    cleaned up. Completed records can be reviewed by the selected date range.
  */
  if (isCompletedLike(ro)) {
    const completedAt = getCompletedOrGoneAt(ro);

    if (dateWindow?.start && dateWindow?.end) {
      return isWithinDateWindow(completedAt, dateWindow);
    }

    return isSameLocalDate(completedAt);
  }

  return true;
}

function getCompletedOrGoneAt(ro) {
  return (
    ro?.vehicleGoneAtISO ||
    ro?.vehicle_gone_at_iso ||
    ro?.pickedUpAtISO ||
    ro?.picked_up_at_iso ||
    ro?.vehicleGoneAt ||
    ro?.vehicle_gone_at ||
    ro?.pickedUpAt ||
    ro?.picked_up_at ||
    ro?.timestamps?.vehicleGoneAt ||
    ro?.timestamps?.vehicle_gone_at ||
    ro?.timestamps?.pickedUpAt ||
    ro?.timestamps?.picked_up_at ||
    ro?.timestamps?.completedAt ||
    ro?.timestamps?.completed_at ||
    ro?.timestamps?.technicianCompletedAt ||
    ro?.timestamps?.technician_completed_at ||
    ro?.technicianCompletedAtISO ||
    ro?.technician_completed_at_iso ||
    ro?.completedAtISO ||
    ro?.completed_at_iso ||
    ro?.completedAt ||
    ro?.completed_at ||
    ro?.closedAt ||
    ro?.closed_at ||
    ro?.updatedAt ||
    ro?.updated_at ||
    ro?.createdAt ||
    ro?.created_at ||
    ""
  );
}

function isSameLocalDate(input, compareTo = new Date()) {
  const d = safeDate(input);
  if (!d) return false;

  return (
    d.getFullYear() === compareTo.getFullYear() &&
    d.getMonth() === compareTo.getMonth() &&
    d.getDate() === compareTo.getDate()
  );
}
const DATE_FILTERS = Object.freeze({
  TODAY: "TODAY",
  YESTERDAY: "YESTERDAY",
  THIS_WEEK: "THIS_WEEK",
  THIS_MONTH: "THIS_MONTH",
  CUSTOM: "CUSTOM",
});

function startOfLocalDay(input = new Date()) {
  const d = safeDate(input) || new Date();
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function addDays(input, days) {
  const d = new Date(input);
  d.setDate(d.getDate() + days);
  return d;
}

function startOfLocalWeekMonday(input = new Date()) {
  const d = startOfLocalDay(input);
  const day = d.getDay(); // Sunday = 0
  const diff = day === 0 ? -6 : 1 - day;
  return addDays(d, diff);
}

function dateInputValue(input = new Date()) {
  const d = safeDate(input) || new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function localDateFromInput(value) {
  const s = safeString(value).trim();
  if (!s) return null;

  const [year, month, day] = s.split("-").map((part) => Number(part));
  if (!year || !month || !day) return null;

  return new Date(year, month - 1, day);
}

function getAdvisorDateWindow(filter, customFrom, customTo) {
  const todayStart = startOfLocalDay();
  const tomorrowStart = addDays(todayStart, 1);

  if (filter === DATE_FILTERS.YESTERDAY) {
    const yesterdayStart = addDays(todayStart, -1);
    return {
      start: yesterdayStart,
      end: todayStart,
      label: "Yesterday",
      includeCarryoverActive: false,
    };
  }

  if (filter === DATE_FILTERS.THIS_WEEK) {
    return {
      start: startOfLocalWeekMonday(),
      end: tomorrowStart,
      label: "This Week",
      includeCarryoverActive: false,
    };
  }

  if (filter === DATE_FILTERS.THIS_MONTH) {
    const now = new Date();
    return {
      start: new Date(now.getFullYear(), now.getMonth(), 1),
      end: tomorrowStart,
      label: "This Month",
      includeCarryoverActive: false,
    };
  }

  if (filter === DATE_FILTERS.CUSTOM) {
    const start = localDateFromInput(customFrom);
    const toStart = localDateFromInput(customTo);
    const end = toStart ? addDays(toStart, 1) : null;

    return {
      start,
      end,
      label:
        customFrom && customTo
          ? `${customFrom} to ${customTo}`
          : "Custom Range",
      includeCarryoverActive: false,
    };
  }

  return {
    start: todayStart,
    end: tomorrowStart,
    label: "Today",
    includeCarryoverActive: true,
  };
}

function isWithinDateWindow(input, dateWindow) {
  const t = toTime(input);
  const start = dateWindow?.start?.getTime?.();
  const end = dateWindow?.end?.getTime?.();

  if (!Number.isFinite(t)) return false;
  if (!Number.isFinite(start) || !Number.isFinite(end)) return true;

  return t >= start && t < end;
}

function getAdvisorDateAnchor(ro) {
  if (isGoneLike(ro) || isCompletedLike(ro)) {
    return getCompletedOrGoneAt(ro);
  }

  return (
    ro?.customerContactedAtISO ||
    ro?.customerContactedAt ||
    ro?.timestamps?.customerContactedAt ||
    ro?.promiseTimeISO ||
    ro?.promiseTime ||
    ro?.timestamps?.createdAt ||
    ro?.createdAtISO ||
    ro?.createdAt ||
    ro?.updatedAtISO ||
    ro?.updatedAt ||
    ""
  );
}

function isWithinAdvisorDateWindow(ro, dateWindow) {
  if (!dateWindow?.start || !dateWindow?.end) return true;

  if (
    dateWindow.includeCarryoverActive &&
    !isGoneLike(ro) &&
    !isCompletedLike(ro)
  ) {
    return true;
  }

  return isWithinDateWindow(getAdvisorDateAnchor(ro), dateWindow);
}
function isWaiter(ro) {
  return String(ro?.transportType || "").toUpperCase() === "WAITER";
}

function isPromiseOverdue(ro) {
  if (!isActiveAdvisorRO(ro)) return false;
  const promise = toTime(ro?.promiseTimeISO || ro?.promiseTime);
  if (!promise) return false;
  return promise < Date.now();
}

function isPromiseRisk(ro) {
  if (!isActiveAdvisorRO(ro)) return false;
  const promise = toTime(ro?.promiseTimeISO || ro?.promiseTime);
  if (!promise) return false;
  return promise <= Date.now() + 60 * 60 * 1000;
}

const SORTS = Object.freeze({
  NEWEST: "NEWEST",
  PROMISE: "PROMISE",
  STATUS: "STATUS",
  CONTACTED: "CONTACTED",
  CUSTOMER: "CUSTOMER",
});

function statusRank(ro) {
  const op = getOperationalState(ro).key;
  if (op === "UNASSIGNED") return 0;
  if (op === "WAITING_PARTS") return 1;
  if (op === "IN_PROGRESS") return 2;
  if (op === "ASSIGNED") return 3;
  if (op === "COMPLETED") return 4;
  if (op === "ARCHIVED") return 5;
  return 6;
}

function compareROs(a, b, sortKey) {
  if (sortKey === SORTS.PROMISE) {
    const aVal = toTime(a?.promiseTimeISO || a?.promiseTime);
    const bVal = toTime(b?.promiseTimeISO || b?.promiseTime);
    const aRank = aVal == null ? Number.POSITIVE_INFINITY : aVal;
    const bRank = bVal == null ? Number.POSITIVE_INFINITY : bVal;
    if (aRank !== bRank) return aRank - bRank;
  }

  if (sortKey === SORTS.STATUS) {
    const aRank = statusRank(a);
    const bRank = statusRank(b);
    if (aRank !== bRank) return aRank - bRank;
  }

  if (sortKey === SORTS.CONTACTED) {
    const aVal = a?.customerContacted ? 1 : 0;
    const bVal = b?.customerContacted ? 1 : 0;
    if (aVal !== bVal) return aVal - bVal;
  }

  if (sortKey === SORTS.CUSTOMER) {
    const aName = safeString(a?.customerName).toLowerCase();
    const bName = safeString(b?.customerName).toLowerCase();
    const cmp = aName.localeCompare(bName);
    if (cmp !== 0) return cmp;
  }

  const aCreated = toTime(a?.timestamps?.createdAt || a?.createdAt) || 0;
  const bCreated = toTime(b?.timestamps?.createdAt || b?.createdAt) || 0;
  return bCreated - aCreated;
}

function compareGoneROs(a, b) {
  const aTime = toTime(getCompletedOrGoneAt(a)) || 0;
  const bTime = toTime(getCompletedOrGoneAt(b)) || 0;
  return bTime - aTime;
}

function makeEditForm(ro) {
  return {
    roNumber: safeString(ro?.roNumber).trim(),
    tagNumber: safeString(ro?.tagNumber).trim(),
    vinLast8: getVinDisplay(ro),
    customerName: safeString(ro?.customerName).trim(),
    vehicle: safeString(ro?.vehicle).trim(),
    advisorName: safeString(ro?.advisorName).trim(),
    advisorEmploymentId: safeString(ro?.advisorEmploymentId || ro?.advisor_employment_id).trim(),
    promiseTimeISO: toDateTimeLocalValue(ro?.promiseTimeISO),
    workMix: safeString(ro?.workMix).trim() || WORK_MIX.OTHER,
    transportType: safeString(ro?.transportType).trim() || TRANSPORT_TYPES.OTHER,
  };
}

/* =========================
   Styles
========================= */
const pageWrapper = {
  padding: 10,
  display: "flex",
  flexDirection: "column",
  gap: 9,
  width: "100%",
  maxWidth: "none",
  margin: 0,
  boxSizing: "border-box",
};

const headerRow = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 10,
  flexWrap: "wrap",
  marginBottom: 2,
};

const eyebrowStyle = {
  fontSize: 10,
  fontWeight: 900,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: "rgba(15,23,42,0.52)",
};

const pageTitleStyle = {
  margin: 0,
  fontSize: 22,
  lineHeight: 1.02,
  fontWeight: 700,
  letterSpacing: "-0.035em",
  color: "rgba(15,23,42,0.94)",
};

const pageSubtitleStyle = {
  margin: "4px 0 0",
  color: "rgba(15,23,42,0.58)",
  fontSize: 11.5,
  lineHeight: 1.32,
};

const cardBase = {
  background: "#ffffff",
  borderRadius: 12,
  padding: "0.62rem 0.7rem",
  boxShadow: "0 10px 24px rgba(15, 23, 42, 0.045)",
  border: "1px solid #e5e7eb",
};

const toolbarStyle = {
  ...cardBase,
  display: "grid",
  gap: 8,
};

const metricGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
  gap: 8,
};

const metricCardStyle = {
  background: "#ffffff",
  border: "1px solid #e5e7eb",
  borderRadius: 12,
  padding: "0.56rem 0.65rem",
  minHeight: 62,
  boxShadow: "0 8px 20px rgba(15, 23, 42, 0.035)",
};

const metricLabelStyle = {
  fontSize: "0.64rem",
  fontWeight: 800,
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  color: "#64748b",
};

const metricValueStyle = {
  marginTop: "0.22rem",
  fontSize: "1.22rem",
  lineHeight: 1,
  fontWeight: 750,
  color: "#0f172a",
};

const metricSubStyle = {
  marginTop: "0.28rem",
  fontSize: "0.72rem",
  color: "#64748b",
  lineHeight: 1.25,
};

const rowBase = {
  display: "grid",
  gridTemplateColumns: "minmax(0, 1fr) auto",
  gap: "0.42rem",
  alignItems: "center",
  padding: "0.36rem 0.5rem",
  borderRadius: 10,
  background: "#ffffff",
  boxShadow: "0 1px 2px rgba(15, 23, 42, 0.025)",
  border: "1px solid #e5e7eb",
};

const sectionTitleStyle = {
  fontSize: "0.68rem",
  fontWeight: 800,
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  color: "#334155",
  marginBottom: "0.25rem",
};

const actionBtnStyle = {
  height: 26,
  padding: "0 0.46rem",
  borderRadius: 8,
  border: "1px solid #d1d5db",
  background: "#ffffff",
  color: "#111827",
  cursor: "pointer",
  fontWeight: 700,
  fontSize: "0.66rem",
  lineHeight: 1,
  whiteSpace: "nowrap",
};

const primaryBtnStyle = {
  ...actionBtnStyle,
  border: "1px solid #111827",
  background: "#111827",
  color: "#fff",
};

const dangerBtnStyle = {
  ...actionBtnStyle,
  border: "1px solid #fed7aa",
  background: "#fff7ed",
  color: "#9a3412",
};

const selectStyle = {
  height: 32,
  padding: "0 0.62rem",
  borderRadius: 9,
  border: "1px solid #d1d5db",
  background: "#ffffff",
  minWidth: 145,
  fontSize: "0.76rem",
  fontWeight: 700,
  fontFamily: "inherit",
};

const searchInputStyle = {
  height: 32,
  padding: "0 0.62rem",
  borderRadius: 9,
  border: "1px solid #d1d5db",
  minWidth: 320,
  flex: "1 1 360px",
  fontSize: "0.76rem",
  background: "#ffffff",
  fontFamily: "inherit",
};

const fieldStyle = {
  padding: "0.55rem 0.7rem",
  borderRadius: 8,
  border: "1px solid #d1d5db",
  background: "#ffffff",
  fontSize: "0.9rem",
  minHeight: 40,
  width: "100%",
  boxSizing: "border-box",
  fontFamily: "inherit",
};

function pillBase() {
  return {
    fontSize: "0.68rem",
    borderRadius: "999px",
    padding: "0.16rem 0.48rem",
    fontWeight: 700,
    border: "1px solid #e5e7eb",
    whiteSpace: "nowrap",
    display: "inline-flex",
    alignItems: "center",
    lineHeight: 1.05,
  };
}

function tagPillStyle(isMissing) {
  const base = pillBase();
  if (isMissing) {
    return {
      ...base,
      borderColor: "#fca5a5",
      background: "#fee2e2",
      color: "#991b1b",
    };
  }

  return {
    ...base,
    borderColor: "#cbd5e1",
    background: "#f8fafc",
    color: "#0f172a",
  };
}

function attentionPillStyle() {
  return {
    ...pillBase(),
    borderColor: "#fca5a5",
    background: "#fee2e2",
    color: "#991b1b",
  };
}

function successPillStyle() {
  return {
    ...pillBase(),
    borderColor: "#86efac",
    background: "#dcfce7",
    color: "#166534",
  };
}

function warnPillStyle() {
  return {
    ...pillBase(),
    borderColor: "#fdba74",
    background: "#ffedd5",
    color: "#9a3412",
  };
}

function operationalPillStyle(kind) {
  const base = pillBase();

  if (kind === "UNASSIGNED") {
    return {
      ...base,
      borderColor: "#bfdbfe",
      background: "#eff6ff",
      color: "#1d4ed8",
    };
  }

  if (kind === "WAITING_PARTS") {
    return {
      ...base,
      borderColor: "#fdba74",
      background: "#ffedd5",
      color: "#9a3412",
    };
  }

  if (kind === "IN_PROGRESS") {
    return {
      ...base,
      borderColor: "#93c5fd",
      background: "#dbeafe",
      color: "#1e40af",
    };
  }

  if (kind === "COMPLETED") {
    return {
      ...base,
      borderColor: "#cbd5e1",
      background: "#f1f5f9",
      color: "#334155",
    };
  }

  if (kind === "ARCHIVED") {
    return {
      ...base,
      borderColor: "#cbd5e1",
      background: "#f8fafc",
      color: "#64748b",
    };
  }

  return {
    ...base,
    borderColor: "#cbd5e1",
    background: "#f8fafc",
    color: "#334155",
  };
}

function modalOverlayStyle() {
  return {
    position: "fixed",
    inset: 0,
    background: "rgba(15,23,42,0.38)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 18,
    zIndex: 9999,
  };
}

function modalCardStyle() {
  return {
    width: 920,
    maxWidth: "100%",
    maxHeight: "88vh",
    overflow: "hidden",
    background: "#fff",
    border: "1px solid #dbe3ee",
    borderRadius: 18,
    boxShadow: "0 26px 60px rgba(15,23,42,0.22)",
    display: "grid",
    gridTemplateRows: "auto minmax(0,1fr) auto",
  };
}

function modalHeaderStyle() {
  return {
    padding: "14px 16px",
    borderBottom: "1px solid #e5e7eb",
    display: "flex",
    alignItems: "flex-start",
    gap: 12,
  };
}

function modalBodyStyle() {
  return {
    padding: 16,
    overflow: "auto",
    display: "grid",
    gap: 14,
  };
}

function modalFooterStyle() {
  return {
    padding: "14px 16px",
    borderTop: "1px solid #e5e7eb",
    display: "flex",
    justifyContent: "flex-end",
    gap: 8,
    flexWrap: "wrap",
  };
}

function btnStyle(kind = "default", disabled = false) {
  let border = "#d1d5db";
  let background = "#fff";
  let color = "#111827";

  if (kind === "primary") {
    border = "#111827";
    background = "#111827";
    color = "#fff";
  } else if (kind === "danger") {
    border = "#dc2626";
    color = "#b91c1c";
  } else if (kind === "warn") {
    border = "#fdba74";
    background = "#fff7ed";
    color = "#9a3412";
  }

  if (disabled) {
    border = "#e5e7eb";
    background = "#f9fafb";
    color = "#9ca3af";
  }

  return {
    minHeight: 28,
    padding: "0 8px",
    borderRadius: 8,
    border: `1px solid ${border}`,
    background,
    color,
    cursor: disabled ? "not-allowed" : "pointer",
    fontWeight: 700,
    fontSize: "0.68rem",
    whiteSpace: "nowrap",
  };
}

function detailFieldCardStyle() {
  return {
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    padding: 12,
    background: "#fff",
    minWidth: 0,
  };
}

function detailLabelStyle() {
  return {
    fontSize: "0.72rem",
    fontWeight: 800,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "#64748b",
    marginBottom: 6,
  };
}

function detailValueStyle() {
  return {
    fontSize: "0.9rem",
    color: "#111827",
    fontWeight: 700,
    wordBreak: "break-word",
  };
}

function errorTextStyle() {
  return {
    marginTop: 6,
    fontSize: 12,
    color: "#991b1b",
    fontWeight: 800,
  };
}

function helperTextStyle() {
  return { marginTop: 6, fontSize: 12, color: "#6b7280" };
}

/* =========================
   Component
========================= */
export default function AdvisorLog() {
  const store = useROStore();
  const auth = useAuth();
  const {
    hasUnread,
    isHydrated: unreadHydrated,
    storeId: unreadStoreId,
  } = useROUnread();
  const ros = useMemo(() => normalizeROArray(store), [store]);

  const currentUser = store?.currentUser || {};
  const actorRole = safeString(currentUser?.role || "advisor");
  const actorName = safeString(currentUser?.name || "Advisor");

  const updateRO = store?.updateRO;
  const {
    sendMessage,
    retryMessage,
    getThreadState,
    canSendMessages,
    isReadOnly: messagesReadOnly,
    storeId: messageStoreId,
  } = useROMessages();

  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState(SORTS.PROMISE);
  const [dateFilter, setDateFilter] = useState(DATE_FILTERS.TODAY);
  const [customFromDate, setCustomFromDate] = useState(() => dateInputValue());
  const [customToDate, setCustomToDate] = useState(() => dateInputValue());
  const [selectedAdvisor, setSelectedAdvisor] = useState(
    actorName || "All Advisors"
  );
  const [sharedNoteDraftByRO, setSharedNoteDraftByRO] = useState({});
  const [noteOpenByRO, setNoteOpenByRO] = useState({});
  const [inlineSendByRO, setInlineSendByRO] = useState({});
  const [flashByRO, setFlashByRO] = useState({});
  const currentUnreadStoreId = safeString(
    auth?.effectiveStoreId || auth?.activeStoreId
  ).trim();
  const unreadStoreMatches =
    !!currentUnreadStoreId &&
    safeString(unreadStoreId).trim() === currentUnreadStoreId;
  const flashTimeoutsRef = useRef({});
  const inlineSubmitLocksRef = useRef(new Set());
  const inlineLastSubmitRef = useRef(new Map());

  const [selectedROId, setSelectedROId] = useState(null);
  const [threadROId, setThreadROId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [modalBanner, setModalBanner] = useState(null);
  const [editForm, setEditForm] = useState(makeEditForm({}));
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    const timeouts = flashTimeoutsRef.current;
    return () => {
      Object.values(timeouts).forEach((timeoutId) =>
        window.clearTimeout(timeoutId)
      );
    };
  }, []);

  const selectedRO = useMemo(() => {
    if (!selectedROId) return null;
    return ros.find((r) => String(r?.id) === String(selectedROId)) || null;
  }, [ros, selectedROId]);

  const threadRO = useMemo(() => {
    if (!threadROId) return null;
    return ros.find((r) => String(r?.id) === String(threadROId)) || null;
  }, [ros, threadROId]);

  const getInlineTrackedMessage = useCallback(
    (roId) => {
      const cleanRoId = safeString(roId).trim();
      const tracked = cleanRoId ? inlineSendByRO[cleanRoId] : null;
      if (!tracked) return null;

      const trackedStoreId = safeString(tracked?.storeId).trim();
      const trackedRoId = safeString(tracked?.roId).trim();

      if (
        trackedStoreId !== safeString(messageStoreId).trim() ||
        trackedRoId !== cleanRoId
      ) {
        return null;
      }

      const thread = getThreadState(cleanRoId);
      const messages = Array.isArray(thread?.messages) ? thread.messages : [];

      const byLocalId = messages.find(
        (message) =>
          safeString(message?.localId).trim() &&
          safeString(message.localId).trim() === safeString(tracked.localId).trim()
      );
      if (byLocalId) return byLocalId;

      const byClientMessageId = messages.find(
        (message) =>
          safeString(message?.storeId).trim() === safeString(tracked.storeId).trim() &&
          safeString(message?.roId).trim() === safeString(tracked.roId).trim() &&
          safeString(message?.authorUserId).trim() ===
            safeString(tracked.authorUserId).trim() &&
          safeString(message?.clientMessageId).trim() ===
            safeString(tracked.clientMessageId).trim()
      );

      if (byClientMessageId?.id) {
        return { ...byClientMessageId, status: "sent" };
      }

      return byClientMessageId || { ...tracked, status: "sending" };
    },
    [getThreadState, inlineSendByRO, messageStoreId]
  );

  useEffect(() => {
    setInlineSendByRO({});
    inlineSubmitLocksRef.current.clear();
    inlineLastSubmitRef.current.clear();
  }, [messageStoreId]);

  useEffect(() => {
    const timers = [];

    Object.entries(inlineSendByRO).forEach(([roId, tracked]) => {
      const message = getInlineTrackedMessage(roId);
      if (message?.status !== "sent") return;

      const timeoutId = window.setTimeout(() => {
        setInlineSendByRO((prev) => {
          const current = prev[roId];
          if (
            safeString(current?.localId).trim() !== safeString(tracked?.localId).trim()
          ) {
            return prev;
          }

          const next = { ...prev };
          delete next[roId];
          return next;
        });
      }, 2500);

      timers.push(timeoutId);
    });

    return () => {
      timers.forEach((timeoutId) => window.clearTimeout(timeoutId));
    };
  }, [getInlineTrackedMessage, inlineSendByRO]);

  const advisorOptions = useMemo(() => {
    const names = new Set();

    ros.forEach((r) => {
      const name = advisorDisplayName(r);
      if (name && name !== "—") names.add(name);
    });

    if (actorName) names.add(actorName);

    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [ros, actorName]);

  const advisorEditOptions = useMemo(() => {
    const options = [];
    const seen = new Set();
    const addOption = ({ value, label, name, employmentId }) => {
      const cleanValue = safeString(value).trim();
      const cleanLabel = safeString(label).trim();
      if (!cleanValue || !cleanLabel || seen.has(cleanValue)) return;
      seen.add(cleanValue);
      options.push({
        value: cleanValue,
        label: cleanLabel,
        name: safeString(name || cleanLabel).trim(),
        employmentId: safeString(employmentId).trim(),
      });
    };
    const addLegacyName = (name) => {
      const cleanName = safeString(name).trim();
      if (!cleanName || cleanName === "—") return;
      addOption({
        value: `name:${cleanName}`,
        label: cleanName,
        name: cleanName,
        employmentId: "",
      });
    };

    const directory = Array.isArray(store?.staffDirectory) ? store.staffDirectory : [];
    directory.forEach((person) => {
      const role = safeString(person?.role).toLowerCase().trim();
      const employmentStatus = safeString(
        person?.employmentStatus ?? person?.employment_status
      ).toUpperCase();
      const isActive =
        person?.active === undefined ? employmentStatus !== "TERMINATED" : !!person.active;
      const employmentId = safeString(person?.id).trim();
      const name = safeString(person?.name).trim();
      if (
        isActive &&
        employmentId &&
        name &&
        (role === "advisor" || role === "service_advisor" || role === "service advisor")
      ) {
        addOption({
          value: `staff:${employmentId}`,
          label: name,
          name,
          employmentId,
        });
      }
    });

    ros.forEach((r) => addLegacyName(advisorDisplayName(r)));
    addLegacyName(actorName);
    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [store?.staffDirectory, ros, actorName]);

  const advisorEditSelectValue = editForm.advisorEmploymentId
    ? `staff:${editForm.advisorEmploymentId}`
    : editForm.advisorName
    ? `name:${editForm.advisorName}`
    : "";

  const applyAdvisorEditSelection = useCallback(
    (value) => {
      const selected = advisorEditOptions.find((option) => option.value === value);
      setEditForm((m) => ({
        ...m,
        advisorName: selected?.name || safeString(value).replace(/^name:/, ""),
        advisorEmploymentId: selected?.employmentId || "",
      }));
    },
    [advisorEditOptions]
  );

  const flash = useCallback((roId, msg) => {
    if (!roId) return;

    if (flashTimeoutsRef.current[roId]) {
      window.clearTimeout(flashTimeoutsRef.current[roId]);
    }

    setFlashByRO((m) => ({ ...m, [roId]: msg }));

    flashTimeoutsRef.current[roId] = window.setTimeout(() => {
      setFlashByRO((m) => {
        const next = { ...m };
        delete next[roId];
        return next;
      });
      delete flashTimeoutsRef.current[roId];
    }, 1800);
  }, []);

  const dateWindow = useMemo(
    () => getAdvisorDateWindow(dateFilter, customFromDate, customToDate),
    [dateFilter, customFromDate, customToDate]
  );

  const scopedROs = useMemo(() => {
    const selected = normalizeName(selectedAdvisor);

    return ros.filter((ro) => {
      const advisorMatches =
        !selectedAdvisor ||
        selectedAdvisor === "All Advisors" ||
        normalizeName(advisorDisplayName(ro)) === selected;

      if (!advisorMatches) return false;

      return isWithinAdvisorDateWindow(ro, dateWindow);
    });
  }, [ros, selectedAdvisor, dateWindow]);

  const activeROs = useMemo(
    () => scopedROs.filter((ro) => isActiveAdvisorRO(ro, dateWindow)),
    [scopedROs, dateWindow]
  );

  const openAdvisorROs = useMemo(
    () =>
      activeROs.filter((ro) => {
        const pickupStatus = safeString(ro?.customerPickupStatus).toUpperCase();
        const advisorAction = safeString(ro?.advisorActionStatus).toUpperCase();

        return (
          pickupStatus !== "CONTACTED_FOR_PICKUP" &&
          advisorAction !== "CUSTOMER_CONTACTED" &&
          !ro?.customerContacted
        );
      }),
    [activeROs]
  );

  const contactedPickupROs = useMemo(
    () =>
      activeROs.filter((ro) => {
        const pickupStatus = safeString(ro?.customerPickupStatus).toUpperCase();
        const advisorAction = safeString(ro?.advisorActionStatus).toUpperCase();

        return (
          pickupStatus === "CONTACTED_FOR_PICKUP" ||
          advisorAction === "CUSTOMER_CONTACTED" ||
          !!ro?.customerContacted
        );
      }),
    [activeROs]
  );

  const goneROs = useMemo(
    () => scopedROs.filter((ro) => isGoneLike(ro)).sort(compareGoneROs),
    [scopedROs]
  );

  const visibleOpenROs = useMemo(() => {
    const filtered = filterROsByQuery(openAdvisorROs, search);
    return [...filtered].sort((a, b) => compareROs(a, b, sortBy));
  }, [openAdvisorROs, search, sortBy]);

  const visibleContactedROs = useMemo(() => {
    const filtered = filterROsByQuery(contactedPickupROs, search);
    return [...filtered].sort((a, b) => compareROs(a, b, sortBy));
  }, [contactedPickupROs, search, sortBy]);

  const visibleGoneROs = useMemo(() => {
    const filtered = filterROsByQuery(goneROs, search);
    return [...filtered].sort(compareGoneROs);
  }, [goneROs, search]);

  const totalShowing =
    visibleOpenROs.length + visibleContactedROs.length + visibleGoneROs.length;

  const summary = useMemo(() => {
    const overdue = activeROs.filter(isPromiseOverdue);
    const risk = activeROs.filter(isPromiseRisk);
    const waiters = activeROs.filter(isWaiter);

    return {
      active: activeROs.length,
      open: openAdvisorROs.length,
      contacted: contactedPickupROs.length,
      gone: goneROs.length,
      overdue: overdue.length,
      risk: risk.length,
      waiters: waiters.length,
      uncontacted: openAdvisorROs.length,
    };
  }, [activeROs, openAdvisorROs, contactedPickupROs, goneROs]);

  const openModalForRo = useCallback((ro) => {
    if (!ro?.id) return;

    setSelectedROId(ro.id);
    setEditForm(makeEditForm(ro));
    setFieldErrors({});
    setModalBanner(null);
    setIsEditMode(false);
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setIsEditMode(false);
    setModalBanner(null);
    setFieldErrors({});
  }, []);

  const hasUnreadThreadNote = useCallback(
    (ro) => {
      const roId = safeString(ro?.id).trim();
      return !!(unreadHydrated && unreadStoreMatches && roId && hasUnread(roId));
    },
    [hasUnread, unreadHydrated, unreadStoreMatches]
  );

  const openThreadForRo = useCallback(
    (ro) => {
      const roId = ro?.id;
      if (!roId) return;

      setThreadROId(roId);
    },
    []
  );

  const closeThread = useCallback(() => {
    setThreadROId(null);
  }, []);

  const onStartEdit = useCallback(() => {
    if (!selectedRO) return;

    setEditForm(makeEditForm(selectedRO));
    setFieldErrors({});
    setModalBanner(null);
    setIsEditMode(true);
  }, [selectedRO]);

  const onCancelEdit = useCallback(() => {
    if (!selectedRO) return;

    setEditForm(makeEditForm(selectedRO));
    setFieldErrors({});
    setModalBanner(null);
    setIsEditMode(false);
  }, [selectedRO]);

  const validateForm = useCallback(() => {
    const errors = {};

    if (!safeString(editForm.roNumber).trim()) {
      errors.roNumber = "RO number is required.";
    }

    if (!safeString(editForm.tagNumber).trim()) {
      errors.tagNumber = "Tag # is required.";
    }

    if (typeof store?.validateTagNumber === "function") {
      const res = store.validateTagNumber(
        editForm.tagNumber,
        selectedRO?.id || ""
      );

      if (!res?.ok) {
        errors.tagNumber =
          res?.message || "Tag # is already in use on an OPEN RO.";
      }
    }

    const vin = normalizeVinLast8(editForm.vinLast8);

    if (!vin) {
      errors.vinLast8 = "VIN (Last 8) is required.";
    } else if (vin.length !== 8) {
      errors.vinLast8 = "VIN (Last 8) must be exactly 8 characters.";
    }

    if (!safeString(editForm.customerName).trim()) {
      errors.customerName = "Customer name is required.";
    }

    if (!safeString(editForm.vehicle).trim()) {
      errors.vehicle = "Vehicle is required.";
    }

    if (!safeString(editForm.advisorName).trim()) {
      errors.advisorName = "Advisor name is required.";
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }, [editForm, selectedRO?.id, store]);

  const onSaveEdit = useCallback(async () => {
    if (!selectedRO || typeof updateRO !== "function") return;
    if (!validateForm()) return;

    try {
      setIsSaving(true);
      setModalBanner(null);

      const normalizedVin = normalizeVinLast8(editForm.vinLast8);
      const nextWorkMix = safeString(editForm.workMix).trim() || WORK_MIX.OTHER;

      await updateRO(
        selectedRO.id,
        {
          roNumber: safeString(editForm.roNumber).trim(),
          tagNumber: toUpperTrim(editForm.tagNumber),
          vin: normalizedVin,
          vinLast8: normalizedVin,
          vin_last_8: normalizedVin,
          vin_last8: normalizedVin,
          vinLast: normalizedVin,
          customerName: safeString(editForm.customerName).trim(),
          vehicle: safeString(editForm.vehicle).trim(),
          advisorName: safeString(editForm.advisorName).trim(),
          advisorEmploymentId: safeString(editForm.advisorEmploymentId).trim(),
          promiseTimeISO: editForm.promiseTimeISO
            ? new Date(editForm.promiseTimeISO).toISOString()
            : "",
          workMix: nextWorkMix,
          transportType:
            safeString(editForm.transportType).trim() || TRANSPORT_TYPES.OTHER,
          concern:
            nextWorkMix === WORK_MIX.MAINTENANCE
              ? "Maintenance"
              : nextWorkMix === WORK_MIX.DIAGNOSIS
              ? "Diagnosis"
              : "Other",
        },
        "RO_EDITED",
        {
          byRole: actorRole,
          byName: actorName,
        }
      );

      setIsEditMode(false);
      setModalBanner({
        type: "success",
        message: `RO #${safeString(editForm.roNumber).trim()} updated.`,
      });
    } catch (err) {
      setModalBanner({ type: "error", message: String(err?.message || err) });
    } finally {
      setIsSaving(false);
    }
  }, [selectedRO, updateRO, validateForm, editForm, actorRole, actorName]);

  const saveSharedNote = useCallback(
    (ro) => {
      const roId = safeString(ro?.id).trim();
      const note = safeString(sharedNoteDraftByRO[roId]).trim();

      if (!roId || !note || !canSendMessages || messagesReadOnly) return;
      if (inlineSubmitLocksRef.current.has(roId)) return;

      const nowMs = Date.now();
      const lastSubmit = inlineLastSubmitRef.current.get(roId);
      if (
        lastSubmit?.body === note &&
        Number.isFinite(lastSubmit?.at) &&
        nowMs - lastSubmit.at < 1000
      ) {
        return;
      }

      inlineSubmitLocksRef.current.add(roId);

      try {
        const accepted = sendMessage(roId, note);
        inlineLastSubmitRef.current.set(roId, { body: note, at: nowMs });

        if (accepted?.localId || accepted?.clientMessageId) {
          setInlineSendByRO((prev) => ({
            ...prev,
            [roId]: accepted,
          }));
        }

        setSharedNoteDraftByRO((m) => ({ ...m, [roId]: "" }));
        setNoteOpenByRO((m) => ({ ...m, [roId]: false }));
      } catch (err) {
        console.error(err);
        alert(String(err?.message || "Unable to send note."));
      } finally {
        inlineSubmitLocksRef.current.delete(roId);
      }
    },
    [
      canSendMessages,
      messagesReadOnly,
      sendMessage,
      sharedNoteDraftByRO,
    ]
  );

  const retryInlineMessage = useCallback(
    (roId) => {
      const cleanRoId = safeString(roId).trim();
      const message = getInlineTrackedMessage(cleanRoId);
      const localId = safeString(message?.localId).trim();

      if (!cleanRoId || !localId || !canSendMessages || messagesReadOnly) return;
      if (inlineSubmitLocksRef.current.has(cleanRoId)) return;

      inlineSubmitLocksRef.current.add(cleanRoId);

      try {
        const accepted = retryMessage(cleanRoId, localId);
        if (accepted?.localId || accepted?.clientMessageId) {
          setInlineSendByRO((prev) => ({
            ...prev,
            [cleanRoId]: accepted,
          }));
        }
      } catch (err) {
        console.error(err);
        alert(String(err?.message || "Unable to retry note."));
      } finally {
        inlineSubmitLocksRef.current.delete(cleanRoId);
      }
    },
    [canSendMessages, getInlineTrackedMessage, messagesReadOnly, retryMessage]
  );
  const toggleContact = useCallback(
    async (ro, checked) => {
      const roId = ro?.id;
      if (!roId) return;

      const stampISO = checked ? nowISO() : "";

      try {
        if (typeof updateRO !== "function") {
          alert("Missing updateRO in ROContext.");
          return;
        }

        if (checked) {
          await Promise.resolve(
            updateRO(
              roId,
              {
                customerContacted: true,
                customerContactNote: "",
                customerContactedAt: stampISO,
                customerContactedAtISO: stampISO,
                customerContactedByRole: actorRole,
                customerContactedByName: actorName,

                advisorActionStatus: "CUSTOMER_CONTACTED",
                advisor_action_status: "CUSTOMER_CONTACTED",
                customerPickupStatus: "CONTACTED_FOR_PICKUP",
                customer_pickup_status: "CONTACTED_FOR_PICKUP",

                vehicleGone: false,
                vehicle_gone: false,
                pickedUp: false,
                picked_up: false,
                pickedUpToday: false,

                timestamps: {
                  ...(ro?.timestamps && typeof ro.timestamps === "object"
                    ? ro.timestamps
                    : {}),
                  customerContactedAt: stampISO,
                  updatedAt: stampISO,
                },
              },
              "CUSTOMER_CONTACTED",
              {
                byRole: actorRole,
                byName: actorName,
                source: "advisor-log",
                notePreview: "Customer contacted for pick up.",
              }
            )
          );

          flash(roId, "Contacted for Pick Up marked ✅");
          return;
        }

        await Promise.resolve(
          updateRO(
            roId,
            {
              customerContacted: false,
              customerContactNote: "",
              customerContactedAt: "",
              customerContactedAtISO: "",
              customerContactedByRole: "",
              customerContactedByName: "",

              advisorActionStatus: "",
              advisor_action_status: "",
              customerPickupStatus: "",
              customer_pickup_status: "",

              timestamps: {
                ...(ro?.timestamps && typeof ro.timestamps === "object"
                  ? ro.timestamps
                  : {}),
                updatedAt: nowISO(),
              },
            },
            "CUSTOMER_CONTACT_CLEARED",
            {
              byRole: actorRole,
              byName: actorName,
              source: "advisor-log",
            }
          )
        );

        flash(roId, "Contacted for Pick Up cleared ✅");
      } catch (err) {
        console.error(err);
        alert(
          checked
            ? "Unable to mark contacted for pick up."
            : "Unable to clear contacted for pick up."
        );
      }
    },
    [updateRO, actorRole, actorName, flash]
  );

  const markVehicleGone = useCallback(
    async (ro) => {
      const roId = ro?.id;
      if (!roId) return;

      const confirmed = window.confirm(
        `Mark RO #${safeString(
          ro?.roNumber || "—"
        )} as vehicle gone / picked up?

This keeps it visible for today's advisor and WIP views, then removes it from daily active work after the business-day rollover.`
      );

      if (!confirmed) return;

      const pickedUpAt = nowISO();

      try {
        if (typeof updateRO !== "function") {
          alert("Missing updateRO in ROContext.");
          return;
        }

        await Promise.resolve(
          updateRO(
            roId,
            {
              customerPickupStatus: "VEHICLE_GONE",
              customer_pickup_status: "VEHICLE_GONE",
              advisorActionStatus: "VEHICLE_GONE",
              advisor_action_status: "VEHICLE_GONE",

              customerContacted: true,
              customerContactedAt:
                ro?.customerContactedAt ||
                ro?.customerContactedAtISO ||
                pickedUpAt,
              customerContactedAtISO:
                ro?.customerContactedAtISO ||
                ro?.customerContactedAt ||
                pickedUpAt,

              vehicleGone: true,
              vehicle_gone: true,
              pickedUp: true,
              picked_up: true,
              pickedUpToday: true,

              vehicleGoneAtISO: pickedUpAt,
              pickedUpAtISO: pickedUpAt,
              vehicleGoneAt: pickedUpAt,
              pickedUpAt,

              timestamps: {
                ...(ro?.timestamps && typeof ro.timestamps === "object"
                  ? ro.timestamps
                  : {}),
                pickedUpAt,
                vehicleGoneAt: pickedUpAt,
                updatedAt: pickedUpAt,
              },
            },
            "VEHICLE_GONE",
            {
              byRole: actorRole,
              byName: actorName,
              source: "advisor-log",
              notePreview: "Vehicle marked gone / picked up by advisor.",
            }
          )
        );

        flash(roId, "Vehicle marked gone ✅");
      } catch (err) {
        console.error(err);
        alert("Unable to mark vehicle gone.");
      }
    },
    [updateRO, actorRole, actorName, flash]
  );

  const renderFieldOrInput = (label, value, fieldKey, inputNode, helper = "") => {
    const error = fieldErrors[fieldKey] || "";

    return (
      <div style={detailFieldCardStyle()}>
        <div style={detailLabelStyle()}>{label}</div>

        {isEditMode && inputNode ? (
          <>
            {inputNode}
            {error ? (
              <div style={errorTextStyle()}>{error}</div>
            ) : helper ? (
              <div style={helperTextStyle()}>{helper}</div>
            ) : null}
          </>
        ) : (
          <div style={detailValueStyle()}>{value || "—"}</div>
        )}
      </div>
    );
  };

  function renderROCard(ro, index, bucket = "OPEN") {
    const roId = ro?.id;
    const tag = getTag(ro);
    const isMissingTag = !tag;
    const promise = formatPromise(ro?.promiseTimeISO || ro?.promiseTime);
    const op = getOperationalState(ro);
    const transport = transportLabel(ro?.transportType);
    const mix = workMixLabel(ro?.workMix);
    const waiter = isWaiter(ro);
    const contacted = !!ro?.customerContacted;
    const advisorName = advisorDisplayName(ro);
    const latestNote = latestSharedNote(ro?.notes);
    const hasUnreadNote = hasUnreadThreadNote(ro);
    const noteValue = safeString(sharedNoteDraftByRO[roId] ?? "");
    const techName = safeString(ro?.assignedTechName).trim() || "—";
    const overdue = isPromiseOverdue(ro);
    const risk = isPromiseRisk(ro);
    const gone = isGoneLike(ro);
    const completed = isCompletedLike(ro);
    const isNoteOpen = !!noteOpenByRO[roId];
    const showActions = bucket !== "GONE";
    const inlineMessage = getInlineTrackedMessage(roId);
    const inlineStatus = safeString(inlineMessage?.status).trim();
    const inlineCanSend = canSendMessages && !messagesReadOnly;

    return (
      <div
        key={roId || `${ro?.roNumber}-${index}`}
        className="advisor-log-ro-row"
        style={{
          ...rowBase,
          marginBottom: 0,
          borderLeft: overdue
            ? "4px solid #dc2626"
            : gone || completed
            ? "4px solid #64748b"
            : contacted
            ? "4px solid #16a34a"
            : "4px solid #2563eb",
          background: gone || completed ? "#fbfdff" : "#ffffff",
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              display: "flex",
              gap: 6,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <button
              type="button"
              onClick={() => openModalForRo(ro)}
              style={{
                border: "none",
                background: "transparent",
                padding: 0,
                textAlign: "left",
                cursor: "pointer",
                fontWeight: 750,
                fontSize: "0.82rem",
                color: "#111827",
                lineHeight: 1.08,
              }}
            >
              RO #{ro?.roNumber || "—"}
            </button>

            <span style={tagPillStyle(isMissingTag)}>
              {isMissingTag ? "Tag Missing" : `Tag #${tag}`}
            </span>

            <span style={operationalPillStyle(op.key)}>{op.label}</span>

            {waiter ? <span style={attentionPillStyle()}>Waiter</span> : null}
            {overdue ? <span style={attentionPillStyle()}>Overdue</span> : null}
            {!overdue && risk ? <span style={warnPillStyle()}>Promise Risk</span> : null}
            {contacted ? (
              <span style={successPillStyle()}>Contacted for Pick Up</span>
            ) : null}
            {gone ? <span style={operationalPillStyle("ARCHIVED")}>Vehicle Gone</span> : null}

            {roId && flashByRO[roId] ? (
              <span
                style={{
                  fontSize: "0.68rem",
                  fontWeight: 700,
                  color: "#166534",
                }}
              >
                {flashByRO[roId]}
              </span>
            ) : null}
          </div>

          <div
            style={{
              marginTop: 4,
              fontSize: "0.74rem",
              fontWeight: 700,
              color: "#111827",
              lineHeight: 1.2,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {ro?.customerName || "Customer"} — {ro?.vehicle || "Vehicle"}
          </div>

          <div
            style={{
              marginTop: 3,
              display: "flex",
              flexWrap: "wrap",
              gap: "2px 8px",
              fontSize: "0.66rem",
              color: "#64748b",
              lineHeight: 1.25,
            }}
          >
            <span>
              Advisor: <b>{advisorName}</b>
            </span>
            <span>
              Tech: <b>{techName}</b>
            </span>
            <span>
              Promise:{" "}
              <b style={{ color: overdue ? "#991b1b" : "#111827" }}>
                {promise}
              </b>
            </span>
            {transport && !waiter ? (
              <span>
                Transport: <b>{transport}</b>
              </span>
            ) : null}
            <span>
              Work: <b>{mix}</b>
            </span>
            {gone || completed ? (
              <span>
                Vehicle Gone/Completed:{" "}
                <b>{formatShortDateTime(getCompletedOrGoneAt(ro))}</b>
              </span>
            ) : null}
          </div>

          {latestNote ? (
            <div
              style={{
                marginTop: 4,
                fontSize: "0.66rem",
                color: "#64748b",
                background: "#f8fafc",
                border: "1px solid #e5e7eb",
                borderRadius: 8,
                padding: "3px 6px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              <span style={{ fontWeight: 900, color: "#475569" }}>Latest:</span>{" "}
              {latestNote}
            </div>
          ) : null}

          {inlineStatus ? (
            <div
              style={{
                marginTop: 4,
                fontSize: "0.66rem",
                fontWeight: 800,
                color: inlineStatus === "failed" ? "#991b1b" : "#475569",
                display: "flex",
                gap: 6,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              {inlineStatus === "sending" ? "Sending…" : null}
              {inlineStatus === "sent" ? "Sent" : null}
              {inlineStatus === "failed" && inlineMessage?.retryable ? (
                <button
                  type="button"
                  onClick={() => retryInlineMessage(roId)}
                  disabled={!inlineCanSend}
                  style={{
                    border: "none",
                    padding: 0,
                    background: "transparent",
                    color: "#991b1b",
                    font: "inherit",
                    fontWeight: 900,
                    cursor: inlineCanSend ? "pointer" : "not-allowed",
                  }}
                >
                  Failed — Retry
                </button>
              ) : null}
              {inlineStatus === "failed" && !inlineMessage?.retryable ? (
                <span>
                  {inlineMessage?.error ||
                    "Couldn’t retry that note. Send it again as a new note."}
                </span>
              ) : null}
            </div>
          ) : null}

          {showActions && isNoteOpen ? (
            <div
              style={{
                marginTop: 8,
                paddingTop: 8,
                borderTop: "1px solid #f1f5f9",
                display: "flex",
                gap: 8,
                alignItems: "flex-start",
                flexWrap: "wrap",
              }}
            >
              <textarea
                className="advisor-log-note-input"
                value={noteValue}
                disabled={!inlineCanSend}
                onChange={(e) =>
                  setSharedNoteDraftByRO((m) => ({
                    ...m,
                    [roId]: e.target.value,
                  }))
                }
                placeholder='Shared note, ex: "Left voicemail for pickup" or "Customer approved repairs"'
                rows={2}
                style={{
                  padding: "8px 10px",
                  borderRadius: 10,
                  border: "1px solid #ddd",
                  minWidth: 300,
                  flex: 1,
                  resize: "vertical",
                  fontSize: 12.5,
                  fontFamily: "inherit",
                }}
              />

              <button
                type="button"
                onClick={() => saveSharedNote(ro)}
                disabled={!inlineCanSend || !noteValue.trim()}
                style={primaryBtnStyle}
              >
                Save Note
              </button>

              <button
                type="button"
                onClick={() => setNoteOpenByRO((m) => ({ ...m, [roId]: false }))}
                style={actionBtnStyle}
              >
                Cancel
              </button>
            </div>
          ) : null}
        </div>

        <div
          className="advisor-log-ro-actions"
          style={{
            display: "flex",
            gap: 4,
            flexWrap: "wrap",
            justifyContent: "flex-end",
            alignItems: "center",
            minWidth: 215,
          }}
        >
          {showActions ? (
            <>
              <button
                type="button"
                onClick={() => toggleContact(ro, !contacted)}
                style={contacted ? actionBtnStyle : primaryBtnStyle}
                title={
                  contacted
                    ? "Clear contacted-for-pick-up status"
                    : "Mark contacted for pick up"
                }
              >
                {contacted ? "Clear Contact" : "Contacted"}
              </button>

              <button
                type="button"
                onClick={() => openModalForRo(ro)}
                style={actionBtnStyle}
              >
                Open
              </button>

              <button
                type="button"
                onClick={() => openThreadForRo(ro)}
                style={{
                  ...btnStyle("default"),
                  position: "relative",
                  borderColor: hasUnreadNote ? "#fdba74" : "#d1d5db",
                  background: hasUnreadNote ? "#fff7ed" : "#ffffff",
                  color: hasUnreadNote ? "#9a3412" : "#111827",
                }}
                title={
                  hasUnreadNote
                    ? "Unread note activity"
                    : "Open notes and activity"
                }
              >
                Notes / Activity
                {hasUnreadNote ? (
                  <span
                    style={{
                      position: "absolute",
                      top: -5,
                      right: -5,
                      width: 10,
                      height: 10,
                      borderRadius: 999,
                      background: "#f97316",
                      border: "2px solid #ffffff",
                      boxShadow: "0 0 0 1px rgba(249,115,22,0.18)",
                    }}
                  />
                ) : null}
              </button>

              <button
                type="button"
                onClick={() => markVehicleGone(ro)}
                style={dangerBtnStyle}
              >
                Vehicle Gone
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => openModalForRo(ro)}
              style={actionBtnStyle}
            >
              Open History
            </button>
          )}
        </div>
      </div>
    );
  }

  const renderAdvisorSection = (title, subtitle, rows, bucket) => {
    return (
      <section style={cardBase}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
            gap: 8,
            marginBottom: 6,
          }}
        >
          <div>
            <div style={sectionTitleStyle}>{title}</div>
            {subtitle ? (
              <div style={{ fontSize: "0.72rem", color: "#64748b", marginTop: -2 }}>
                {subtitle}
              </div>
            ) : null}
          </div>

          <span style={pillBase()}>{rows.length}</span>
        </div>

        {rows.length === 0 ? (
          <div
            style={{
              padding: "0.5rem 0.6rem",
              border: "1px dashed #cbd5e1",
              borderRadius: 10,
              color: "#64748b",
              background: "#f8fafc",
              fontSize: "0.72rem",
            }}
          >
            No ROs in this section.
          </div>
        ) : (
          <div style={{ display: "grid", gap: 5 }}>
            {rows.map((ro, index) => renderROCard(ro, index, bucket))}
          </div>
        )}
      </section>
    );
  };

  return (
    <div className="advisor-log-page" style={pageWrapper}>
      <header className="advisor-log-header" style={headerRow}>
        <div>
          <div style={eyebrowStyle}>Advisor Workspace</div>
          <h1 style={pageTitleStyle}>Advisor Log</h1>
          <p style={pageSubtitleStyle}>
            Advisor view for pickup contact, notes, follow-up, and vehicle-gone cleanup.
          </p>
        </div>

        <div
          className="advisor-log-header-controls"
          style={{
            display: "flex",
            gap: 8,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <select
            value={selectedAdvisor}
            onChange={(e) => setSelectedAdvisor(e.target.value)}
            style={selectStyle}
          >
            <option value="All Advisors">All Advisors</option>
            {advisorOptions.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>

          <select
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            style={selectStyle}
          >
            <option value={DATE_FILTERS.TODAY}>Date: Today</option>
            <option value={DATE_FILTERS.YESTERDAY}>Date: Yesterday</option>
            <option value={DATE_FILTERS.THIS_WEEK}>Date: This Week</option>
            <option value={DATE_FILTERS.THIS_MONTH}>Date: This Month</option>
            <option value={DATE_FILTERS.CUSTOM}>Date: Custom</option>
          </select>

          {dateFilter === DATE_FILTERS.CUSTOM ? (
            <>
              <input
                type="date"
                value={customFromDate}
                onChange={(e) => setCustomFromDate(e.target.value)}
                style={{ ...selectStyle, minWidth: 145 }}
              />

              <span style={{ fontSize: 12, color: "#64748b", fontWeight: 900 }}>
                to
              </span>

              <input
                type="date"
                value={customToDate}
                onChange={(e) => setCustomToDate(e.target.value)}
                style={{ ...selectStyle, minWidth: 145 }}
              />
            </>
          ) : null}

          <span style={pillBase()}>
            Showing: <b style={{ marginLeft: 4 }}>{totalShowing}</b>
          </span>
        </div>
      </header>

      <section className="advisor-log-metrics" style={metricGrid}>
        <div style={metricCardStyle}>
          <div style={metricLabelStyle}>Open Advisor Work</div>
          <div style={metricValueStyle}>{summary.open}</div>
          <div style={metricSubStyle}>Needs follow-up.</div>
        </div>

        <div style={metricCardStyle}>
          <div style={metricLabelStyle}>Contacted</div>
          <div style={metricValueStyle}>{summary.contacted}</div>
          <div style={metricSubStyle}>Waiting for pickup.</div>
        </div>

        <div
          style={{
            ...metricCardStyle,
            borderColor: summary.overdue ? "#fecaca" : "#e5e7eb",
            background: summary.overdue ? "#fffafa" : "#ffffff",
          }}
        >
          <div style={metricLabelStyle}>Promise Risk</div>
          <div style={metricValueStyle}>{summary.risk}</div>
          <div style={metricSubStyle}>
            {summary.overdue} overdue • {summary.waiters} waiters
          </div>
        </div>

        <div style={metricCardStyle}>
          <div style={metricLabelStyle}>Vehicle Gone</div>
          <div style={metricValueStyle}>{summary.gone}</div>
          <div style={metricSubStyle}>Picked up in selected range.</div>
        </div>
      </section>

      <section className="advisor-log-toolbar" style={toolbarStyle}>
        <div
          className="advisor-log-toolbar-controls"
          style={{
            display: "flex",
            gap: 8,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search RO, tag, customer, vehicle, advisor, tech, status, notes..."
            style={searchInputStyle}
          />

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            style={selectStyle}
          >
            <option value={SORTS.PROMISE}>Sort: Promise Time</option>
            <option value={SORTS.NEWEST}>Sort: Newest</option>
            <option value={SORTS.STATUS}>Sort: Status</option>
            <option value={SORTS.CONTACTED}>Sort: Contact Needed</option>
            <option value={SORTS.CUSTOMER}>Sort: Customer</option>
          </select>
        </div>

        <div style={{ fontSize: "0.72rem", color: "#64748b" }}>
          Showing advisor work for: <b>{dateWindow.label}</b>.
        </div>
      </section>

      {renderAdvisorSection(
        "Open Advisor Work",
        "Open ROs needing advisor follow-up.",
        visibleOpenROs,
        "OPEN"
      )}

      {renderAdvisorSection(
        "Contacted for Pick Up",
        "Customer notified; waiting for pickup.",
        visibleContactedROs,
        "CONTACTED"
      )}

      {renderAdvisorSection(
        dateFilter === DATE_FILTERS.TODAY ? "Vehicle Gone Today" : "Vehicle Gone",
        `Picked up / no longer on lot for ${dateWindow.label}.`,
        visibleGoneROs,
        "GONE"
      )}

      {isModalOpen && selectedRO ? (
        <div style={modalOverlayStyle()} onClick={closeModal}>
          <div style={modalCardStyle()} onClick={(e) => e.stopPropagation()}>
            <div style={modalHeaderStyle()}>
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    alignItems: "center",
                    flexWrap: "wrap",
                  }}
                >
                  <div
                    style={{
                      fontSize: "1.05rem",
                      fontWeight: 700,
                      color: "#111827",
                    }}
                  >
                    RO #{safeString(selectedRO?.roNumber || "—")}
                  </div>

                  {safeString(selectedRO?.tagNumber).trim() ? (
                    <span style={tagPillStyle(false)}>
                      Tag #{safeString(selectedRO?.tagNumber).trim()}
                    </span>
                  ) : null}

                  <span style={operationalPillStyle(getOperationalState(selectedRO).key)}>
                    {getOperationalState(selectedRO).label}
                  </span>
                </div>

                <div style={{ marginTop: 4, fontSize: 12.5, color: "#6b7280" }}>
                  {safeString(selectedRO?.customerName || "—")} •{" "}
                  {safeString(selectedRO?.vehicle || "—")}
                </div>
              </div>

              <button
                type="button"
                onClick={closeModal}
                style={{
                  marginLeft: "auto",
                  width: 36,
                  height: 36,
                  borderRadius: 10,
                  border: "1px solid #d1d5db",
                  background: "#fff",
                  cursor: "pointer",
                  fontWeight: 700,
                }}
              >
                ✕
              </button>
            </div>

            <div style={modalBodyStyle()}>
              {modalBanner ? (
                <div
                  style={{
                    padding: "10px 12px",
                    borderRadius: 12,
                    border: "1px solid #e5e7eb",
                    fontWeight: 700,
                    fontSize: 12.5,
                    background:
                      modalBanner.type === "success" ? "#ecfdf5" : "#fff1f2",
                    color:
                      modalBanner.type === "success" ? "#065f46" : "#991b1b",
                  }}
                >
                  {modalBanner.message}
                </div>
              ) : null}

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 12,
                }}
              >
                {renderFieldOrInput(
                  "RO Number",
                  selectedRO?.roNumber,
                  "roNumber",
                  <input
                    value={editForm.roNumber}
                    onChange={(e) =>
                      setEditForm((m) => ({ ...m, roNumber: e.target.value }))
                    }
                    style={fieldStyle}
                  />
                )}

                {renderFieldOrInput(
                  "Tag Number",
                  selectedRO?.tagNumber,
                  "tagNumber",
                  <input
                    value={editForm.tagNumber}
                    onChange={(e) =>
                      setEditForm((m) => ({ ...m, tagNumber: e.target.value }))
                    }
                    style={fieldStyle}
                  />
                )}

                {renderFieldOrInput(
                  "VIN (Last 8)",
                  getVinDisplay(selectedRO),
                  "vinLast8",
                  <input
                    value={editForm.vinLast8}
                    onChange={(e) =>
                      setEditForm((m) => ({
                        ...m,
                        vinLast8: normalizeVinLast8(e.target.value),
                      }))
                    }
                    style={{ ...fieldStyle, textTransform: "uppercase" }}
                    maxLength={8}
                  />,
                  "Exactly 8 letters/numbers."
                )}

                {renderFieldOrInput(
                  "Advisor",
                  selectedRO?.advisorName,
                  "advisorName",
                  <select
                    value={advisorEditSelectValue}
                    onChange={(e) => applyAdvisorEditSelection(e.target.value)}
                    style={fieldStyle}
                  >
                    <option value="">Select advisor…</option>
                    {advisorEditOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                )}

                {renderFieldOrInput(
                  "Customer",
                  selectedRO?.customerName,
                  "customerName",
                  <input
                    value={editForm.customerName}
                    onChange={(e) =>
                      setEditForm((m) => ({
                        ...m,
                        customerName: e.target.value,
                      }))
                    }
                    style={fieldStyle}
                  />
                )}

                {renderFieldOrInput(
                  "Vehicle",
                  selectedRO?.vehicle,
                  "vehicle",
                  <input
                    value={editForm.vehicle}
                    onChange={(e) =>
                      setEditForm((m) => ({ ...m, vehicle: e.target.value }))
                    }
                    style={fieldStyle}
                  />
                )}

                {renderFieldOrInput(
                  "Promise Time",
                  formatPromise(selectedRO?.promiseTimeISO),
                  "promiseTimeISO",
                  <input
                    type="datetime-local"
                    step={900}
                    value={editForm.promiseTimeISO}
                    onChange={(e) =>
                      setEditForm((m) => ({
                        ...m,
                        promiseTimeISO: e.target.value,
                      }))
                    }
                    style={fieldStyle}
                  />
                )}

                {renderFieldOrInput(
                  "Assigned Tech",
                  selectedRO?.assignedTechName || "—",
                  "assignedTech",
                  null
                )}

                {renderFieldOrInput(
                  "Work Mix",
                  workMixLabel(selectedRO?.workMix),
                  "workMix",
                  <select
                    value={editForm.workMix}
                    onChange={(e) =>
                      setEditForm((m) => ({ ...m, workMix: e.target.value }))
                    }
                    style={fieldStyle}
                  >
                    <option value={WORK_MIX.MAINTENANCE}>Maintenance</option>
                    <option value={WORK_MIX.DIAGNOSIS}>Diagnosis</option>
                    <option value={WORK_MIX.OTHER}>Other</option>
                  </select>
                )}

                {renderFieldOrInput(
                  "Transport Type",
                  selectedRO?.transportType,
                  "transportType",
                  <select
                    value={editForm.transportType}
                    onChange={(e) =>
                      setEditForm((m) => ({
                        ...m,
                        transportType: e.target.value,
                      }))
                    }
                    style={fieldStyle}
                  >
                    <option value={TRANSPORT_TYPES.WAITER}>Waiter</option>
                    <option value={TRANSPORT_TYPES.LOANER}>Loaner</option>
                    <option value={TRANSPORT_TYPES.SHUTTLE}>Shuttle</option>
                    <option value={TRANSPORT_TYPES.OTHER}>Other</option>
                  </select>
                )}

                {renderFieldOrInput("Status", selectedRO?.status, "status", null)}
                {renderFieldOrInput(
                  "Lifecycle",
                  selectedRO?.lifecycle,
                  "lifecycle",
                  null
                )}
              </div>

              <ROStatusTimeline
                ro={selectedRO}
                title="Audit / History Timeline"
                emptyMessage="No history available."
              />
            </div>

            <div style={modalFooterStyle()}>
              {isEditMode ? (
                <>
                  <button
                    type="button"
                    onClick={onCancelEdit}
                    style={btnStyle("default", isSaving)}
                    disabled={isSaving}
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    onClick={onSaveEdit}
                    style={btnStyle("primary", isSaving)}
                    disabled={isSaving}
                  >
                    {isSaving ? "Saving…" : "Save"}
                  </button>
                </>
              ) : (
                <>
                  <button type="button" onClick={closeModal} style={btnStyle()}>
                    Close
                  </button>

                  {isActiveAdvisorRO(selectedRO) ? (
                    <button
                      type="button"
                      onClick={() => markVehicleGone(selectedRO)}
                      style={btnStyle("warn")}
                    >
                      Vehicle Gone
                    </button>
                  ) : null}

                  <button
                    type="button"
                    onClick={onStartEdit}
                    style={btnStyle("primary")}
                  >
                    Edit
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      ) : null}

      <style>
        {`
          @media (max-width: 1180px) {
            .advisor-log-page {
              padding: 8px !important;
            }

            .advisor-log-metrics {
              grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
            }

            .advisor-log-header-controls {
              width: 100%;
              justify-content: flex-start;
            }

            .advisor-log-header-controls select,
            .advisor-log-header-controls input {
              min-width: 0 !important;
              flex: 1 1 170px;
            }
          }

          @media (max-width: 1050px) {
            .advisor-log-ro-row {
              grid-template-columns: 1fr !important;
              align-items: start !important;
            }

            .advisor-log-ro-actions {
              min-width: 0 !important;
              justify-content: flex-start !important;
            }

            .advisor-log-note-input {
              min-width: 0 !important;
              width: 100% !important;
            }

            .advisor-log-toolbar-controls {
              display: grid !important;
              grid-template-columns: minmax(0, 1fr) auto;
              width: 100%;
            }

            .advisor-log-toolbar-controls input {
              min-width: 0 !important;
              width: 100% !important;
            }
          }

          @media (max-width: 900px) {
            .advisor-log-page {
              padding: 4px !important;
              gap: 8px !important;
            }
          }

          @media (max-width: 620px) {
            .advisor-log-metrics,
            .advisor-log-toolbar-controls,
            div[style*="grid-template-columns: 1fr 1fr"] {
              grid-template-columns: 1fr !important;
            }
          }
        `}
      </style>

      <ROActivityDrawer
        ro={threadRO}
        isOpen={!!threadRO}
        onClose={closeThread}
        currentUser={currentUser}
        actorRole={actorRole}
        actorName={actorName}
        title="Advisor RO Thread"
      />
    </div>
  );
}
